import { gql } from '@apollo/client';

/**
 * The staff console's own reads.
 *
 * A deliberate note on `getAllUsers`: app_admin selected the whole user row,
 * which includes the TPIN hash, the password-reset token, the magic-link
 * token and the live MFA code. None of that is displayed, and pulling it into
 * a browser cache is a liability rather than a feature, so this selects only
 * the fields the screens actually show.
 */
export const GET_ALL_USERS = gql`
  query GetAllUsers {
    getAllUsers {
      total
      items {
        id
        name
        email
        phoneNumber
        userType
        systemGeneratedId
        organizationId
        isActive
        isSuperUser
        emailVerified
        kycVerified
        kycPanMasked
        languagePreference
        createdAt
        updatedAt
      }
    }
  }
`;

/** Who this staff member is, as the employee register knows them. */
export const ME_EMPLOYEE = gql`
  query MeEmployee {
    meEmployee {
      id
      userId
      employeeUsername
      displayName
      orgRoles
      isActive
      isPaused
    }
  }
`;

export const LIST_EMPLOYEES = gql`
  query ListEmployees(
    $employeeType: EmployeeTypeEnum
    $orgRole: EmployeeOrgRoleEnum
    $functionalRole: EmployeeFunctionalRoleEnum
    $isActive: Boolean
    $limit: Int = 100
    $offset: Int = 0
  ) {
    listEmployees(
      employeeType: $employeeType
      orgRole: $orgRole
      functionalRole: $functionalRole
      isActive: $isActive
      limit: $limit
      offset: $offset
    ) {
      id
      userId
      employeeUsername
      displayName
      phoneNumber
      employeeType
      orgRoles
      functionalRoles
      isActive
      isPaused
      emailVerified
      createdAt
      updatedAt
    }
  }
`;

export const LIST_PROSPECTS = gql`
  query ListProspects($filter: ListProspectsFilterInput) {
    listProspects(filter: $filter) {
      total
      items {
        id
        name
        email
        phoneNumber
        company
        status
        source
        assignedTo
        waitlistPosition
        convertedUserId
        convertedAt
        campaignName
        referredBy
        prospectTags
        notes
        createdAt
        updatedAt
      }
    }
  }
`;

/**
 * Conversations the assistant handed to a human.
 *
 * Every row is a customer waiting: the agent could not finish the job and
 * escalated. `escalationState` says whether anybody has picked it up.
 */
export const SUPPORT_ESCALATION_QUEUE = gql`
  query SupportEscalationQueue($limit: Int) {
    supportEscalationQueue(limit: $limit) {
      id
      subject
      category
      priority
      escalationState
      threadName
      roomName
      conversationSummary
      lastMessageAt
      createdAt
    }
  }
`;

/** One staff record. */
export const GET_EMPLOYEE = gql`
  query GetEmployee($id: Int!) {
    getEmployee(id: $id) {
      id
      userId
      employeeUsername
      displayName
      phoneNumber
      employeeType
      orgRoles
      functionalRoles
      isActive
      isPaused
      emailVerified
      createdAt
      updatedAt
    }
  }
`;

/**
 * The role vocabularies, from the server rather than a hard-coded list.
 *
 * app_admin shipped fallback arrays for these and they have already drifted
 * from the schema; reading them is the only way the form cannot offer a role
 * the backend will reject.
 */
export const GET_EMPLOYEE_ENUMS = gql`
  query GetEmployeeEnums {
    getEmployeeOrgRoleEnum { value displayValue description }
    getEmployeeFunctionalRoleEnum { value displayValue description }
    getEmployeeTypeEnum { value displayValue description }
  }
`;
